import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { reportsAPI, bibleAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '@/lib/i18n';
import {
  Users, CalendarCheck, HandCoins, Calendar, Megaphone,
  MessageCircleHeart, Phone, BookOpen, Plus, ArrowRight
} from 'lucide-react';

export default function DashboardPage() {
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  const [dailyVerse, setDailyVerse] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, verseRes] = await Promise.all([
          reportsAPI.dashboard(),
          bibleAPI.dailyVerse()
        ]);
        setStats(statsRes.data);
        setDailyVerse(verseRes.data);
      } catch (err) {
        console.error('Dashboard error:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="page-header animate-pulse h-32" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[1,2,3,4].map(i => <div key={i} className="kpi-card h-24 animate-pulse bg-muted" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Welcome header */}
      <div className="page-header">
        <div className="relative z-10">
          <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight" data-testid="dashboard-welcome">
            {t('welcome')}, {user?.first_name}!
          </h1>
          <p className="text-muted-foreground mt-1">{t('whatsHappening')}</p>
          {isAdmin && (
            <div className="flex flex-wrap gap-2 mt-4">
              <Button size="sm" onClick={() => navigate('/members')} data-testid="quick-action-members">
                <Plus className="w-3 h-3 mr-1" /> {t('newMember')}
              </Button>
              <Button size="sm" variant="secondary" onClick={() => navigate('/attendance')} data-testid="quick-action-attendance">
                <CalendarCheck className="w-3 h-3 mr-1" /> {t('recordAttendance')}
              </Button>
              <Button size="sm" variant="secondary" onClick={() => navigate('/announcements')} data-testid="quick-action-announcement">
                <Megaphone className="w-3 h-3 mr-1" /> {t('announcement')}
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="kpi-card animate-fade-up stagger-1" data-testid="dashboard-members-kpi">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
              <Users className="w-4 h-4 text-primary" />
            </div>
          </div>
          <div className="kpi-number">{(stats?.total_members || 0).toLocaleString()}</div>
          <div className="kpi-label">{t('totalMembers')}</div>
        </div>

        <div className="kpi-card animate-fade-up stagger-2" data-testid="dashboard-attendance-kpi">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-lg bg-accent/50 flex items-center justify-center">
              <CalendarCheck className="w-4 h-4 text-accent-foreground" />
            </div>
          </div>
          <div className="kpi-number">{(stats?.today_attendance || 0).toLocaleString()}</div>
          <div className="kpi-label">{t('todayAttendance')}</div>
        </div>

        <div className="kpi-card animate-fade-up stagger-3" data-testid="dashboard-giving-kpi">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-lg bg-green-50 flex items-center justify-center">
              <HandCoins className="w-4 h-4 text-green-700" />
            </div>
          </div>
          <div className="kpi-number">${(stats?.month_giving || 0).toLocaleString()}</div>
          <div className="kpi-label">{t('thisMonthGiving')}</div>
        </div>

        <div className="kpi-card animate-fade-up stagger-4" data-testid="dashboard-groups-kpi">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
              <Calendar className="w-4 h-4 text-blue-700" />
            </div>
          </div>
          <div className="kpi-number">{(stats?.total_groups || 0).toLocaleString()}</div>
          <div className="kpi-label">{t('activeGroups')}</div>
        </div>
      </div>

      {/* Content Grid */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Daily Verse */}
        <Card className="quiet-card">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="font-display text-lg flex items-center gap-2">
                <BookOpen className="w-4 h-4 text-primary" />
                Daily Verse
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={() => navigate('/bible')}>
                {t('readBible')} <ArrowRight className="w-3 h-3 ml-1" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {dailyVerse ? (
              <div>
                <p className="text-base leading-7 italic text-foreground/80">"{dailyVerse.text?.trim()}"</p>
                <p className="text-sm font-semibold text-primary mt-3">— {dailyVerse.reference}</p>
                {dailyVerse.reflection && (
                  <div className="mt-4 pt-4 border-t border-border">
                    <p className="text-sm leading-relaxed text-foreground/70">{dailyVerse.reflection}</p>
                  </div>
                )}
                {dailyVerse.prayer && (
                  <div className="mt-3 p-3 bg-primary/5 rounded-lg">
                    <p className="text-xs font-medium text-primary mb-1">Prayer</p>
                    <p className="text-sm italic text-foreground/60">{dailyVerse.prayer}</p>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-muted-foreground">Loading verse...</p>
            )}
          </CardContent>
        </Card>

        {/* Recent Announcements */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="font-display text-lg flex items-center gap-2">
                <Megaphone className="w-4 h-4 text-primary" />
                Recent Announcements
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={() => navigate('/announcements')}>
                View All <ArrowRight className="w-3 h-3 ml-1" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {stats?.recent_announcements?.length > 0 ? (
              <div className="space-y-3">
                {stats.recent_announcements.slice(0, 3).map(ann => (
                  <div key={ann.id} className="flex items-start gap-3 p-3 rounded-lg bg-muted/50">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-sm">{ann.title}</p>
                        {ann.is_pinned && <Badge variant="secondary" className="text-xs">Pinned</Badge>}
                      </div>
                      <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{ann.content}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-muted-foreground text-sm">No announcements yet</p>
            )}
          </CardContent>
        </Card>

        {/* Quick Links */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="font-display text-lg">Quick Links</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-2">
              <Button variant="outline" className="h-auto py-3 flex flex-col gap-1" onClick={() => navigate('/giving')}>
                <HandCoins className="w-5 h-5" />
                <span className="text-xs">Give</span>
              </Button>
              <Button variant="outline" className="h-auto py-3 flex flex-col gap-1" onClick={() => navigate('/prayer-requests')}>
                <MessageCircleHeart className="w-5 h-5" />
                <span className="text-xs">Prayer</span>
              </Button>
              <Button variant="outline" className="h-auto py-3 flex flex-col gap-1" onClick={() => navigate('/consultations')}>
                <Phone className="w-5 h-5" />
                <span className="text-xs">Consultation</span>
              </Button>
              <Button variant="outline" className="h-auto py-3 flex flex-col gap-1" onClick={() => navigate('/media')}>
                <BookOpen className="w-5 h-5" />
                <span className="text-xs">Sermons</span>
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Upcoming Events */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="font-display text-lg flex items-center gap-2">
                <Calendar className="w-4 h-4 text-primary" />
                Upcoming Events
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={() => navigate('/events')}>
                View All <ArrowRight className="w-3 h-3 ml-1" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {stats?.upcoming_events?.length > 0 ? (
              <div className="space-y-3">
                {stats.upcoming_events.map(evt => (
                  <div key={evt.id} className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Calendar className="w-4 h-4 text-primary" />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-sm">{evt.title}</p>
                      <p className="text-xs text-muted-foreground">{evt.start_date} • {evt.location || 'Church'}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-muted-foreground text-sm">No upcoming events</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
